import React from 'react';
import { observer } from 'mobx-react';
import { Checkbox, Divider, Form } from 'antd';
import { useBytesPerKbSelectOptions, useCompanyInfo, useProfileIsRight } from '../../../hooks';
import { apiMessagesGetFormRules, rulesIsNumber, rulesOptionalMoreThanZero } from '../../../middleware';
import { tF } from '../../../translate';
import { Catcher, FormSelect, InputNumberDebounce } from '../../../Components';

// ToDo: 19.03.2026 -  add translate label
export const EditCompanyInfoTenantConfig: React.FC = observer(() => {
    const isRoot = useProfileIsRight('ROOT');
    const { isTenantConfig, hideDataUsage, hideSmsUsage, hideVoiceUsage } = useCompanyInfo();
    const minimumBillingDataUnitsTitle = tF('Minimum billing data units');
    const validateMinimumBillingDataUnits = [rulesIsNumber, rulesOptionalMoreThanZero, ...apiMessagesGetFormRules];
    const onlyOneUnitTypeIsVisible =
        [hideDataUsage, hideSmsUsage, hideVoiceUsage].filter((hidden) => !hidden).length === 1;

    return isRoot ? (
        <Catcher>
            <Form.Item
                name={'isTenantConfig'}
                valuePropName="checked"
                rules={apiMessagesGetFormRules}
                data-test={'EditCompanyInfoHideCostsData'}
            >
                <Checkbox data-test={'EditCompanyInfoTenantConfigEnableCheckBox'}>
                    {tF('Enable Tenant Config')}
                </Checkbox>
            </Form.Item>
            {isTenantConfig ? (
                <>
                    <Divider>{tF('Tenant config')}</Divider>
                    <FormSelect
                        dataTest={'EditCompanyInfoTenantConfigBytesPerKbData'}
                        fieldName={'bytesPerKb'}
                        isNotClear
                        isShortForm
                        label={tF('Multiplier for KB/MB/GB')}
                        required
                        rules={apiMessagesGetFormRules}
                        useSelectHook={useBytesPerKbSelectOptions}
                        disabled={!isTenantConfig}
                    />
                    <Form.Item
                        name={'minimumBillingDataUnits'}
                        label={minimumBillingDataUnitsTitle}
                        rules={validateMinimumBillingDataUnits}
                        data-test={'EditCompanyInfoTenantConfigMinimumBillingDataUnits'}
                    >
                        <InputNumberDebounce
                            type="number"
                            min={1}
                            step={1}
                            placeholder={minimumBillingDataUnitsTitle}
                            data-test={'EditCompanyInfoTenantConfigMinimumBillingDataUnitsInput'}
                        />
                    </Form.Item>
                    <Form.Item
                        name={'hideCosts'}
                        valuePropName="checked"
                        rules={apiMessagesGetFormRules}
                        data-test={'EditCompanyInfoHideCostsData'}
                    >
                        <Checkbox data-test={'EditCompanyInfoTenantConfigHideCostsCheckBox'}>
                            {tF('Hide costs column for CDRs')}
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        name={'hideDataUsage'}
                        valuePropName="checked"
                        rules={apiMessagesGetFormRules}
                        data-test={'EditCompanyInfoTenantConfigHideDataUsage'}
                    >
                        <Checkbox
                            data-test={'EditCompanyInfoTenantConfigHideDataUsageCheckBox'}
                            disabled={onlyOneUnitTypeIsVisible && !hideDataUsage}
                        >
                            {tF('Hide Data usage')}
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        name={'hideSmsUsage'}
                        valuePropName="checked"
                        rules={apiMessagesGetFormRules}
                        data-test={'EditCompanyInfoTenantConfigHideSmsUsage'}
                    >
                        <Checkbox
                            data-test={'EditCompanyInfoTenantConfigHideSmsUsageCheckBox'}
                            disabled={onlyOneUnitTypeIsVisible && !hideSmsUsage}
                        >
                            {tF('Hide SMS usage')}
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        name={'hideVoiceUsage'}
                        valuePropName="checked"
                        rules={apiMessagesGetFormRules}
                        data-test={'EditCompanyInfoTenantConfigHideVoiceUsage'}
                    >
                        <Checkbox
                            data-test={'EditCompanyInfoTenantConfigHideVoiceUsageCheckBox'}
                            disabled={onlyOneUnitTypeIsVisible && !hideVoiceUsage}
                        >
                            {tF('Hide Voice usage')}
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        name={'showPackageData'}
                        valuePropName="checked"
                        rules={apiMessagesGetFormRules}
                        data-test={'EditCompanyInfoTenantConfigShowPackageData'}
                    >
                        <Checkbox data-test={'EditCompanyInfoTenantConfigShowPackageDataCheckBox'}>
                            {tF('Show Product ID and Package Type in tariffs')}
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        name={'requireTariff'}
                        valuePropName="checked"
                        rules={apiMessagesGetFormRules}
                        data-test={'EditCompanyInfoTenantConfigRequireTariff'}
                    >
                        <Checkbox data-test={'EditCompanyInfoTenantConfigRequireTariffCheckBox'}>
                            {tF('Require tariff')}
                        </Checkbox>
                    </Form.Item>
                </>
            ) : null}
        </Catcher>
    ) : null;
});
